import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, NotificationDispatch, TextField } from '@tryhackme/thm-ui-components';
import { useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import type { CategorizationRule } from 'src/common/interfaces/categorization-dashboard';
import { useSaveRuleMutation } from '../../categorization.slice';
import { StyledButton } from '../accordion/accordion.styles';
import { Group } from '../group-condition/group';
import { NestedRule } from '../nested-rule-condition/nested-rule';
import { Condition } from '../single-condition/condition';
import { categorizationRuleValidation, groupConditionInitialValue, nestedRuleInitialValue, singleConditionInitialValue } from './rule.constants';
import { StyledButtonsGroup, StyledForm, StyledFormGroup, StyledRow } from './rule.styles';
import type { RuleProps } from './rule.types';
export const Rule = ({
  sources,
  rooms,
  rule
}: RuleProps) => {
  const methods = useForm({
    resolver: yupResolver(categorizationRuleValidation),
    defaultValues: rule,
    mode: 'all'
  });
  const {
    control,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: {
      errors
    }
  } = methods;
  const [condition, contexts] = useWatch({
    control,
    name: ['condition', 'contexts']
  });
  const selectedRooms = useMemo(() => rooms.filter(option => contexts?.includes(option.value)), [contexts, rooms]);
  const [saveRule, {
    isLoading
  }] = useSaveRuleMutation();
  const handleAddGroupCondition = () => {
    setValue('condition', groupConditionInitialValue);
  };
  const handleAddSingleCondition = () => {
    setValue('condition', singleConditionInitialValue);
  };
  const handleAddNestedRule = () => setValue('condition', nestedRuleInitialValue);
  const handleRemoveCondition = () => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setValue('condition', undefined);
  };
  const onSubmit = async (data: CategorizationRule) => {
    try {
      await saveRule(data).unwrap();
      reset(data);
      NotificationDispatch('success', 'Rule successfully saved');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        // eslint-disable-next-line no-console
        console.error(error);
        NotificationDispatch('error', 'Something went wrong');
      }
    }
  };
  const renderCondition = () => {
    switch (condition?.type) {
      case 'group':
        {
          return <Group path="condition" sources={sources} currentRuleId={rule._id} handleRemoveCondition={handleRemoveCondition} />;
        }
      case 'single':
        {
          return <Condition path="condition" sources={sources} handleRemove={handleRemoveCondition} />;
        }
      case 'rule':
        {
          return <NestedRule path="condition" currentRuleId={rule._id} handleRemove={handleRemoveCondition} />;
        }
      default:
        {
          return null;
        }
    }
  };
  return <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-component="Rule" data-sentry-source-file="rule.tsx">
      <StyledForm data-sentry-element="StyledForm" data-sentry-source-file="rule.tsx">
        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="rule.tsx">
          <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="rule.tsx">
            <TextField {...register('title')} data-testid="title" id="title" placeholder="Title" variant="input" isRequired maxLength={500} isError={Boolean(errors.title)} data-sentry-element="TextField" data-sentry-source-file="rule.tsx" />

            <TextField {...register('category')} data-testid="category" id="category" placeholder="Category" variant="input" isRequired maxLength={500} isError={Boolean(errors.category)} data-sentry-element="TextField" data-sentry-source-file="rule.tsx" />
          </StyledFormGroup>
        </StyledRow>

        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="rule.tsx">
          <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="rule.tsx">
            <TextField {...register('description')} data-testid="description" id="description" placeholder="Description" variant="textarea" maxLength={200} isError={Boolean(errors.description)} data-sentry-element="TextField" data-sentry-source-file="rule.tsx" />
          </StyledFormGroup>
        </StyledRow>

        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="rule.tsx">
          <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="rule.tsx">
            <Controller control={control} name="contexts" render={({
            field: {
              onChange
            }
          }) => <TextField placeholder="Rooms" variant="select" options={rooms} selected={selectedRooms} isMulti isSearchable handleSelection={option => onChange(option?.map(form => form.value))} isError={Boolean(errors.contexts)} />} data-sentry-element="Controller" data-sentry-source-file="rule.tsx" />
          </StyledFormGroup>
        </StyledRow>

        {renderCondition()}

        {!condition && <StyledButtonsGroup>
            <StyledButton variant="text" color="secondary" size="small" onClick={handleAddGroupCondition}>
              <FontAwesomeIcon icon={faPlus} />
              Add group
            </StyledButton>

            <StyledButton variant="text" color="secondary" size="small" onClick={handleAddSingleCondition}>
              <FontAwesomeIcon icon={faPlus} />
              Add condition
            </StyledButton>

            <StyledButton variant="text" color="secondary" size="small" onClick={handleAddNestedRule}>
              <FontAwesomeIcon icon={faPlus} />
              Add nested rule
            </StyledButton>
          </StyledButtonsGroup>}

        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="rule.tsx">
          <Button variant="solid" color="add" isDisabled={!condition || isLoading}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleSubmit(onSubmit)} data-sentry-element="Button" data-sentry-source-file="rule.tsx">
            Save changes
          </Button>
        </StyledRow>
      </StyledForm>
    </FormProvider>;
};